/**
 * @graphql agg
 *
 * AGG (Atlassian GraphQL Gateway) batch user query.
 */

import { graphql } from 'react-relay';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { queryInterop } from '@confluence/apollo-relay-interop/entry-points/query.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';

import { reportError } from '../../utils/reportError.ts';

import type {
	blockServiceAggUserQueryFetchUsersBatchQuery$data,
	blockServiceAggUserQueryFetchUsersBatchQuery$variables,
} from './__generated__/blockServiceAggUserQueryFetchUsersBatchQuery.graphql.ts';

const RelayBlockServiceAggUserQueryFetchUsersBatchQuery = graphql`
	query blockServiceAggUserQueryFetchUsersBatchQuery($accountIds: [ID!]!) {
		users(accountIds: $accountIds) {
			accountId
			name
			picture
		}
	}
`;

type BatchUserInfo = {
	accountId: string;
	displayName: string | null | undefined;
	avatarUrl: string | null | undefined;
};

/** Maximum number of account IDs per AGG users batch request. */
const AGG_USERS_BATCH_LIMIT = 90;

function chunk<T>(array: T[], size: number): T[][] {
	const chunks: T[][] = [];
	for (let i = 0; i < array.length; i += size) {
		chunks.push(array.slice(i, i + size));
	}
	return chunks;
}

function mapUsersToBatchUserInfo(
	users: blockServiceAggUserQueryFetchUsersBatchQuery$data['users'] | null | undefined,
): BatchUserInfo[] {
	if (!users || !Array.isArray(users)) {
		return [];
	}

	return users
		.filter((user) => user != null && Boolean(user.accountId))
		.map((user) => ({
			accountId: user.accountId,
			displayName: user.name ?? user.accountId,
			avatarUrl: user.picture ?? undefined,
		}));
}

async function fetchUsersBatchSingle(batchAccountIds: string[]): Promise<BatchUserInfo[]> {
	const { data, error } = await queryInterop<
		blockServiceAggUserQueryFetchUsersBatchQuery$data,
		blockServiceAggUserQueryFetchUsersBatchQuery$variables
	>({
		query: RelayBlockServiceAggUserQueryFetchUsersBatchQuery,
		variables: { accountIds: batchAccountIds },
		interopApolloAggClient: true,
	});

	error?.graphQLErrors?.forEach((graphQLError) => {
		logFetchError(graphQLError);
		markErrorAsHandled(graphQLError);
	});

	return mapUsersToBatchUserInfo(data?.users);
}

/**
 * Fetches user info for multiple account IDs via AGG batch request(s).
 * When there are more than 90 account IDs, multiple batches are requested in parallel.
 * Returns an array of users returned by the API; missing/deleted users are omitted.
 */
export const fetchUsersBatch = async (accountIds: string[]): Promise<BatchUserInfo[]> => {
	if (accountIds.length === 0) {
		return [];
	}
	try {
		const batches = chunk(accountIds, AGG_USERS_BATCH_LIMIT);
		const results = await Promise.all(batches.map(fetchUsersBatchSingle));
		return results.flat();
	} catch (error) {
		logFetchError(error);
		markErrorAsHandled(error);
		return [];
	}
};

const logFetchError = (maybeError: unknown) => {
	const message =
		typeof maybeError === 'object' && maybeError !== null && 'message' in maybeError
			? String(maybeError.message)
			: String(maybeError);
	const error =
		maybeError instanceof Error ? maybeError : new Error(message, { cause: maybeError });
	const errorToLog = new Error(
		`Error when fetching Snippet owner user details: ${error?.message || JSON.stringify(error)}`,
	);
	reportError(errorToLog);
};
